import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { LumaLogoIcon } from '../LumaLogo';

const COLS = 14;
const ROWS = 9;
const TOTAL = COLS * ROWS;

const LedGridAnimation = () => {
  const [activeDots, setActiveDots] = useState(() => new Set());

  const tick = useCallback(() => {
    setActiveDots((prev) => {
      const next = new Set(prev);
      const toAdd = Math.floor(Math.random() * 6) + 4;
      const toRemove = Math.floor(Math.random() * 4) + 2;
      for (let i = 0; i < toAdd; i++) {
        next.add(Math.floor(Math.random() * TOTAL));
      }
      const arr = [...next];
      for (let i = 0; i < toRemove && arr.length > 0; i++) {
        next.delete(arr[Math.floor(Math.random() * arr.length)]);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(tick, 280);
    return () => clearInterval(id);
  }, [tick]);

  const getColor = (i) => {
    const r = i % 5;
    if (r === 0 || r === 3) return '#8B5CF6';
    if (r === 1 || r === 4) return '#3B82F6';
    return '#F8FAFC';
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gridTemplateRows: `repeat(${ROWS}, 1fr)`,
          width: '100%',
          height: '100%',
        }}
      >
        {Array.from({ length: TOTAL }, (_, i) => {
          const active = activeDots.has(i);
          const color = getColor(i);
          return (
            <div
              key={i}
              style={{
                borderRight: '1px solid rgba(139,92,246,0.08)',
                borderBottom: '1px solid rgba(139,92,246,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: '3px',
                  height: '3px',
                  borderRadius: '50%',
                  backgroundColor: active ? color : 'rgba(139,92,246,0.15)',
                  boxShadow: active ? `0 0 8px 3px ${color}80` : 'none',
                  transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VIDEO_URL = '/hero-video.mp4';

export const HeroSection = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Layer 1: Video background — muted (sound removed) */}
      <video
        ref={videoRef}
        data-testid="hero-video"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Layer 2: Dark cinematic overlay over video */}
      <div
        className="absolute inset-0"
        style={{
          zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.50) 50%, rgba(10,10,10,0.95) 100%)',
        }}
      />

      {/* Layer 3: LED Grid overlay */}
      <div className="absolute inset-0" style={{ zIndex: 2, opacity: 0.6 }}>
        <LedGridAnimation />
      </div>

      {/* Layer 4: Ambient glow orbs */}
      <div
        className="absolute pointer-events-none animate-orb-1"
        style={{
          zIndex: 3,
          top: '20%',
          left: '15%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute pointer-events-none animate-orb-2"
        style={{
          zIndex: 3,
          bottom: '15%',
          right: '10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          zIndex: 3,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 60%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Layer 5: Content */}
      <div className="relative flex flex-col items-center text-center px-6 max-w-5xl mx-auto" style={{ zIndex: 10 }}>
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="mb-8 animate-float"
          data-testid="hero-logo"
        >
          <LumaLogoIcon size={72} />
        </motion.div>

        {/* Label */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-6 font-medium"
          data-testid="hero-label"
        >
          Immersive LED Experiences
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
          className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-none tracking-tighter text-white mb-6 text-glow-white"
          data-testid="hero-headline"
        >
          Step Into
          <br />
          <span className="gradient-text">The Light</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="font-manrope text-base sm:text-lg text-white/60 max-w-xl mb-12 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Not just a dancefloor — experience a moment people remember.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="flex flex-col sm:flex-row gap-4"
          data-testid="hero-ctas"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollTo('#booking')}
            className="btn-primary text-white font-manrope font-semibold text-sm px-8 py-4 rounded-full tracking-wide animate-pulse-glow"
          >
            Book Your Event
          </button>
          <button
            data-testid="hero-experience-btn"
            onClick={() => scrollTo('#experience')}
            className="btn-outline text-white font-manrope font-semibold text-sm px-8 py-4 rounded-full tracking-wide"
          >
            View Experience
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.7 }}
        onClick={() => scrollTo('#experience')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
        style={{ zIndex: 10 }}
        data-testid="hero-scroll-indicator"
      >
        <span className="text-white/30 text-xs font-manrope tracking-[0.2em] uppercase group-hover:text-white/60 transition-colors duration-200">
          Scroll
        </span>
        <ChevronDown size={18} className="text-white/30 animate-scroll-bounce group-hover:text-luma-purple transition-colors duration-200" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
